import { toString } from 'mdast-util-to-string';
import type { Root, RootContent } from 'mdast';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import remarkParse from 'remark-parse';
import { unified } from 'unified';

export interface MarkdownTextPart {
  type: 'text';
  content: string;
}

export interface MarkdownMermaidPart {
  type: 'mermaid';
  content: string;
}

export type MarkdownPart = MarkdownTextPart | MarkdownMermaidPart;

export interface ParsedMarkdownTextPart extends MarkdownTextPart {
  tree: Root;
}

export type ParsedMarkdownPart = ParsedMarkdownTextPart | MarkdownMermaidPart;

const markdownProcessor = unified()
  .use(remarkParse)
  .use(remarkGfm)
  .use(remarkMath)
  .use(remarkBreaks);

export function splitMarkdownParts(content: string): MarkdownPart[] {
  const text = content?.toString?.() ?? '';
  const mermaidRegex = /\/mermaid\{([\s\S]*?)\}/g;
  const parts: MarkdownPart[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = mermaidRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push({
        type: 'text',
        content: text.slice(lastIndex, match.index),
      });
    }

    parts.push({
      type: 'mermaid',
      content: match[1].trim(),
    });

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push({
      type: 'text',
      content: text.slice(lastIndex),
    });
  }

  return parts.length > 0 ? parts : [{ type: 'text', content: text }];
}

export function parseMarkdown(content: string): Root {
  const tree = markdownProcessor.parse(content);
  return markdownProcessor.runSync(tree) as Root;
}

export function parseMarkdownParts(content: string): ParsedMarkdownPart[] {
  return splitMarkdownParts(content).map((part) => {
    if (part.type === 'mermaid') {
      return part;
    }

    return {
      ...part,
      tree: parseMarkdown(part.content),
    };
  });
}

export function markdownNodeToPlainText(node: Root | RootContent): string {
  return toString(node).trim();
}